import { oc } from '@orpc/contract';
import { ACCOUNT_STATUSES } from '@lsc/domain';
import { z } from 'zod';
export const draftCandidate = z.object({
    accountId: z.uuid(),
    label: z.string(),
    legalName: z.string().nullable(),
    legalEntityId: z.uuid().nullable(),
    processor: z.string(),
    mid: z.string().nullable(),
    city: z.string().nullable(),
    status: z.enum(ACCOUNT_STATUSES),
});
export const draftEntity = z.object({ legalEntityId: z.uuid().nullable(), legalName: z.string().nullable(), accounts: z.array(draftCandidate) });
export const draftAutoTarget = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('account'), accountId: z.uuid(), label: z.string() }),
    z.object({ kind: z.literal('entity'), legalEntityId: z.uuid(), label: z.string() }),
]);
export const DRAFT_BUCKETS = ['live', 'closed_only', 'none'];
export const DISMISS_REASONS = ['closed', 'not_merchant', 'no_match'];
export const draftRow = z.object({
    draftId: z.uuid(),
    e164: z.string(),
    companyName: z.string().nullable(),
    contactName: z.string().nullable(),
    email: z.string().nullable(),
    source: z.string(),
    notes: z.string().nullable(),
    createdAt: z.string(),
    /** When this number last called Support (any leg, inbound), or null if it never has. The queue sorts on it. */
    lastCallAt: z.string().nullable(),
    ticketCount: z.number().int(),
    /** Accounts the import's notes named, matched by name and grouped by the cleanup rules. A suggestion only. */
    bucket: z.enum(DRAFT_BUCKETS),
    live: z.array(draftEntity),
    closed: z.array(draftCandidate),
    auto: draftAutoTarget.nullable(),
    nameExact: z.boolean(),
});
export const draftListInput = z.object({ limit: z.number().int().min(1).max(200).default(50), cursor: z.string().optional() });
export const draftListOutput = z.object({
    rows: z.array(draftRow),
    total: z.number().int(),
    /** How many of `total` have a live candidate, closed candidates only, or none, so the page can say what the work is. */
    buckets: z.object({ live: z.number().int(), closed_only: z.number().int(), none: z.number().int() }),
    nextCursor: z.string().nullable(),
});
export const draftResolveOutput = z.object({ ok: z.literal(true), ticketsMoved: z.number().int() });
export const draftLinkInput = z
    .object({ draftId: z.uuid(), accountId: z.uuid().optional(), legalEntityId: z.uuid().optional() })
    .refine((v) => (v.accountId ? 1 : 0) + (v.legalEntityId ? 1 : 0) === 1, { message: 'Link to one account or one company.' });
export const draftsContract = {
    list: oc.route({ method: 'POST', path: '/drafts/list', summary: 'Pending recognised callers, most recently calling first' }).input(draftListInput).output(draftListOutput),
    link: oc.route({ method: 'POST', path: '/drafts/link', summary: 'Link a recognised caller to an account, or to a company so every location answers' }).input(draftLinkInput).output(draftResolveOutput),
    dismiss: oc
        .route({ method: 'POST', path: '/drafts/dismiss', summary: 'Dismiss a recognised caller' })
        .input(z.object({ draftId: z.uuid(), reason: z.enum(DISMISS_REASONS).optional() }))
        .output(draftResolveOutput),
    dismissMany: oc
        .route({ method: 'POST', path: '/drafts/dismiss-many', summary: 'Dismiss several recognised callers at once' })
        .input(z.object({ draftIds: z.array(z.uuid()).min(1).max(200), reason: z.enum(DISMISS_REASONS).optional() }))
        .output(z.object({ ok: z.literal(true), dismissed: z.number().int(), skipped: z.number().int() })),
};
//# sourceMappingURL=drafts.js.map